import { InfoCircleOutlined } from "@ant-design/icons";
import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Alert, Section, SectionContent, SectionHeader } from "@impero-com/ui";

import { NotificationFrequency } from "api/generated/model";

import * as theme from "./NotificationFrequencySettings.css";
import { useUpdateUserSettingsMutation } from "./queries/useUpdateUserSettingsMutation";
import { useUserSettingsQuery } from "./queries/useUserSettingsQuery";

interface NotificationFrequencySettingsProps {
    userId: number;
    // Use "you"/"your" wording for profile page
    profilePage?: boolean;
}

export function NotificationFrequencySettings({
    userId,
    profilePage = false,
}: NotificationFrequencySettingsProps) {
    const { t } = useTranslation();
    const { data: userSettings } = useUserSettingsQuery(userId);
    const updateUserSettings = useUpdateUserSettingsMutation();

    const options: { value: NotificationFrequency; label: string }[] = [
        {
            value: "Immediate",
            label: t("NotificationFrequency_Immediate"),
        },
        {
            value: "Daily",
            label: t("NotificationFrequency_Daily"),
        },
        {
            value: "Weekly",
            label: t("NotificationFrequency_Weekly"),
        },
    ];
    const descriptions: Record<NotificationFrequency, string> = {
        Immediate: t("NotificationFrequency_Immediate_Description"),
        Daily: t("NotificationFrequency_Daily_Description"),
        Weekly: t("NotificationFrequency_Weekly_Description"),
    };

    return (
        <Section>
            <SectionHeader
                level={4}
                titleLabel={t("MyProfile_NotificationFrequency_Title")}
                subtitleLabel={
                    profilePage
                        ? t("MyProfile_NotificationFrequency_Description")
                        : t("UserManagement_NotificationFrequency_Description")
                }
                showBorder
            />
            <SectionContent>
                <SelectInput<NotificationFrequency>
                    label={t("MyProfile_NotificationFrequency_Label")}
                    className={theme.select}
                    value={userSettings.notificationFrequency}
                    onChange={(notificationFrequency) =>
                        updateUserSettings.mutate({
                            userId,
                            data: { notificationFrequency },
                        })
                    }
                    options={options}
                    nodeAfter={
                        <p className={theme.description}>
                            {descriptions[userSettings.notificationFrequency]}
                        </p>
                    }
                />
                <Alert
                    type="info"
                    icon={<InfoCircleOutlined />}
                    message={t("MyProfile_NotificationFrequency_Alert")}
                />
            </SectionContent>
        </Section>
    );
}
